import type { NoteIntent } from '../notes/intents';
import type { NoteStatus } from '../notes/status';

/**
 * `'descartadas'` is not a status and not an intent: a discarded note keeps the status it had,
 * so it is a different *list* rather than a different slice of the same one - which is exactly
 * how `GET /notes?discarded=true` answers it.
 */
export type NotesFilter = 'all' | 'descartadas' | NoteStatus | NoteIntent;

export const DISCARDED_FILTER = 'descartadas';

export interface NotesScreenState {
  readonly filter: NotesFilter;
  readonly scrollY: number;
}

/**
 * Remembers the note list's filter and scroll position across a navigate-away-and-back
 * (`docs/implementation/06-mobile-web.md` 6.3), in the spirit of
 * `capture/captureAttemptStore.ts`: a plain module-level variable outlives the screen's own
 * component instance without needing a page reload to survive, or a context provider threaded
 * through the router.
 *
 * Unlike the capture store, this one is read-and-kept rather than read-and-cleared: the owner
 * may go back and forth between Captura and Notas many times in one visit, and every return
 * should land on the same filter and roughly the same scroll offset, not just the first one.
 * Pagination position is not tracked here at all - it survives for free, because
 * `NotesScreen`'s `useInfiniteQuery` cache entry (keyed by filter) is not torn down on unmount;
 * it is only ever a background refetch away from being current again.
 */
let saved: NotesScreenState | null = null;

export function saveNotesScreenState(state: NotesScreenState): void {
  saved = state;
}

export function readNotesScreenState(): NotesScreenState | null {
  return saved;
}
